import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GlobalContext } from 'context';
import {
  toPascalCase,
  SignOutHandler,
  UserDetails,
  NavigateConst,
  getDecryptedValue,
  isGuest,
} from 'common';
import {
  Button,
  UserIconBlack,
  DropdownToggleIcon,
  Paragraph,
  TickIcon
} from '@sentara-health/optimahealth_web';

interface ProfileDropdownProps {
  isMobileView?: boolean;
  onMyProfileClick?: () => void;
}

const ProfileDropdown = ({
  isMobileView = false,
  onMyProfileClick,
}: ProfileDropdownProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const signOut = SignOutHandler();
  const {
    getFeature,
    profileState,
    selectedProxyAccount,
    setSelectedProxyAccount,
    approvedDesignators,
  } = useContext(GlobalContext);
  const [guestValue, setGuestValue] = useState<boolean>(false);
  const [fullName, setFullName] = useState('');
  const [firstName, setFirstName] = useState('');
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);

  useEffect(() => {
    (async () => {
      const personalDetails = await getDecryptedValue(UserDetails.guest);
      const personalInfo = JSON.parse(personalDetails ?? '{}');
      const first = personalInfo?.user?.firstName || profileState?.firstName || '';
      const last = personalInfo?.user?.lastName || profileState?.lastName || '';

      setFirstName(toPascalCase(first));
      setFullName(
        `${toPascalCase(first)} ${toPascalCase(last)}`.trim()
      );
      const guest: boolean = await isGuest();
      setGuestValue(guest);
    })();
  }, [profileState?.firstName, profileState?.lastName]);

  // Reset selected proxy account if current selection is invalid
  useEffect(() => {
    if (
      selectedProxyAccount !== 'current' &&
      approvedDesignators.length > 0 &&
      !approvedDesignators.some(
        (designator: { name: string; status: string }) => designator.name === selectedProxyAccount
      )
    ) {
      setSelectedProxyAccount('current');
    }
  }, [approvedDesignators, selectedProxyAccount, setSelectedProxyAccount]);

  const navigateToProfile = () => {
    onMyProfileClick?.();
    navigate(NavigateConst.profile);
  };

  const handleAccountSwitch = (account: string) => {
    setSelectedProxyAccount(account);
    // Navigate to dashboard when switching accounts
    navigate(NavigateConst.dashboard);
  };

  const mobileMenuClass = isMobileExpanded
    ? 'show position-static w-100'
    : 'position-static w-100';

  return (
    <div
      className={`position-relative ${
        isMobileView ? 'd-block w-100' : 'd-xl-block d-lg-block d-md-block'
      }`}
    >
      <div
        className={
          isMobileView
            ? 'd-flex flex-column align-items-stretch w-100'
            : 'd-inline-flex align-items-center'
        }
      >
        <Button
          iconLeft={isMobileView ? undefined : <UserIconBlack />}
          iconRight={<DropdownToggleIcon />}
          dataTestId="profileDropdownButton"
          id={isMobileView ? undefined : 'profileDropdown'}
          type="button"
          variant="dropdownmenu_btn"
          data-bs-toggle={isMobileView ? undefined : 'dropdown'}
          aria-expanded={isMobileView ? isMobileExpanded : false}
          className={isMobileView ? 'w-100 d-flex justify-content-center' : ''}
          onClick={
            isMobileView
              ? () => setIsMobileExpanded((prevState) => !prevState)
              : undefined
          }
        >
          <Paragraph
            level={2}
            text={`${t('common.hello')}, ${firstName}`}
            variant="bold"
            className="text-truncate mb-0"
          />
        </Button>

        <div
          className={`dropdown-menu dropdown-menu-custom mt-2 ${
            isMobileView ? mobileMenuClass : 'dropdown-menu-end'
          }`}
          aria-labelledby={isMobileView ? undefined : 'profileDropdown'}
          style={isMobileView ? undefined : { minWidth: '300px' }}
        >
          {getFeature?.MyProfile && !guestValue && (
            <Button
              dataTestId="myProfileButton"
              variant="dropdown-item"
              className="text-start px-3 py-2 "
              onClick={navigateToProfile}
            >
              <Paragraph
                level={2}
                text={t('common.viewProfile')}
                variant="semiBold"
                className="w-100 text-start mb-0"
              />
            </Button>
          )}

          {getFeature?.DrProfileSwitch && (
            <>
              <div className="pe-none text-start ">
                <Paragraph
                  level={1}
                  text={t('profile.designatedRepresentative.switchAccount')}
                  variant="light"
                  data-testid="switchAccountHeader"
                  className="w-100 px-3 py-2 mb-0 text-start"
                  textColor="disable"
                />
              </div>

              <Button
                className="mw-auto d-block py-2 px-3"
                variant="dropdown-item"
                onClick={() => handleAccountSwitch('current')}
              >
                <div className="row w-100">
                  <div className="col-2">
                    {selectedProxyAccount === 'current' && (
                      <TickIcon />
                    )}
                  </div>
                  <div className="col-auto">
                    <Paragraph
                      level={2}
                      text={fullName}
                      variant="semiBold"
                      className="w-100 text-start mb-0"
                    />
                  </div>
                </div>
              </Button>

              {approvedDesignators.map((designator: { name: string; status: string }) => (
                <Button
                  key={designator.name}
                  className="mw-auto d-block py-2 px-3"
                  variant="dropdown-item"
                  onClick={() => handleAccountSwitch(designator.name)}
                >
                  <div className="row w-100">
                    <div className="col-2">
                      {selectedProxyAccount === designator.name && <TickIcon />}
                    </div>
                    <div className="col-auto">
                      <Paragraph
                        level={2}
                        text={designator.name}
                        variant="semiBold"
                        className="d-inline-block m-0"
                      />
                      <Paragraph
                        level={1}
                        variant="light"
                        data-testid="switchAccountHeader"
                        className="d-inline-block ms-1 m-0"
                        textColor="disable"
                      />
                    </div>
                  </div>
                </Button>
              ))}
            </>
          )}

          <div>
            <Button
              dataTestId="myProfileButton"
              variant="dropdown-item"
              className="text-start px-3 py-2 "
              onClick={signOut}
            >
              <Paragraph
                level={2}
                text="Sign Out"
                variant="semiBold"
                className="w-100 text-start mb-1"
              />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileDropdown;